// frontend/src/services/noBreak.service.ts - DEFERRED VALIDATION LIKE OVERTIME
import {
  collection,
  doc,
  query,
  where,
  getDocs,
  getDoc,
  updateDoc,
  setDoc,
  addDoc,
  serverTimestamp,
  Timestamp,
  limit
} from 'firebase/firestore';
import { db } from './firebase';

export interface NoBreakEmployee {
  id: string;
  name: string;
  designation: string;
  department: string;
  employeeNumber?: string;
  pin?: string;
  employeePin?: string;
  
  // 🔄 REMOVED: No more attendance checking during load
  // hasAttendanceToday?: boolean;
  // attendanceStatus?: string;
  
  uuid?: string; // Store UUID for later validation
}

export interface NoBreakRequestData {
  projectName: string;
  projectCode: string;
  selectedDate: Date;
  employeeIds: string[];
  requesterId: string;
  requesterName: string;
  reason: string;
  shift?: string;
}

export interface NoBreakResult {
  success: boolean;
  message: string;
  requestId?: string;
  processedCount?: number;
  failedEmployees?: string[];
  details?: any[];
}

export interface AttendanceRecord {
  employeeId: string;
  date: string;
  checkIn?: Date;
  checkInLocation?: string;
  workStatus: string;
  noBreak?: boolean;
  noBreakReason?: string;
  noBreakAppliedBy?: string;
  noBreakAppliedAt?: Date;
  lastUpdated?: Date;
  [key: string]: any;
}

class NoBreakService {
  private employeesCache: NoBreakEmployee[] = [];
  private pinToUUIDMap = new Map<string, string>();
  private lastCacheUpdate = 0;
  private readonly CACHE_TTL = 10 * 60 * 1000; // 10 minutes
  private isInitializing = false;

  /**
   * 🆕 UPDATED: Initialize service WITHOUT attendance checking (like overtime)
   */
  private async initializeService(): Promise<void> {
    const now = Date.now();
    
    // Check if cache is still valid
    if (this.employeesCache.length > 0 && (now - this.lastCacheUpdate) < this.CACHE_TTL) {
      console.log('Using cached employee data');
      return;
    }

    // Prevent multiple simultaneous initializations
    if (this.isInitializing) {
      console.log('Initialization already in progress, waiting...');
      while (this.isInitializing) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return;
    }

    this.isInitializing = true;

    try {
      console.log('🚀 Initializing No Break service (NO attendance checking)...');
      
      // Step 1: Build PIN -> UUID mapping ONCE
      await this.buildPinToUUIDMap();
      
      // Step 2: Load employees from MasterSheet ONLY
      await this.loadEmployeesFromMasterSheet();
      
      // 🔄 REMOVED: No attendance checking during initialization
      // Validation will happen when offline data syncs (like overtime)
      
      this.lastCacheUpdate = now;
      console.log(`✅ Service initialized with ${this.employeesCache.length} employees (attendance will be validated when data syncs)`);
      
    } catch (error) {
      console.error('❌ Error initializing service:', error);
      throw error;
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Build PIN to UUID mapping efficiently
   */
  private async buildPinToUUIDMap(): Promise<void> {
    if (this.pinToUUIDMap.size > 0) {
      console.log('PIN to UUID map already built');
      return;
    }

    console.log('🔄 Building PIN -> UUID mapping...');
    
    try {
      const employeesSnapshot = await getDocs(collection(db, 'employees'));
      console.log(`📊 Found ${employeesSnapshot.size} employees in employees collection`);
      
      employeesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const pin = data.employeePin || data.pin || data.employeeNumber;
        
        if (pin) {
          // Store multiple pin formats for flexible matching
          const cleanPin = pin.toString().trim();
          const pinWithoutZeros = cleanPin.replace(/^0+/, '') || '0';
          
          this.pinToUUIDMap.set(cleanPin, doc.id);
          this.pinToUUIDMap.set(pinWithoutZeros, doc.id);
          this.pinToUUIDMap.set(`0${cleanPin}`, doc.id); // Add leading zero variant
        }
      });
      
      console.log(`✅ Built PIN mapping with ${this.pinToUUIDMap.size} entries`);
    } catch (error) {
      console.error('❌ Error building PIN mapping:', error);
      throw error;
    }
  }

  /**
   * 🆕 UPDATED: Load employees WITHOUT attendance checking (like overtime)
   */
  private async loadEmployeesFromMasterSheet(): Promise<void> {
    console.log('📋 Loading employees from MasterSheet (NO attendance check)...');
    
    try {
      const masterSheetSnapshot = await getDocs(
        collection(db, 'MasterSheet', 'Employee-Data', 'employees')
      );

      console.log(`Found ${masterSheetSnapshot.docs.length} employees in MasterSheet`);

      this.employeesCache = [];

      masterSheetSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const docId = doc.id; // EMP3576

        // Extract PIN from various sources
        let pin = data.pin || data.employeeNumber || '';
        if (!pin && docId.startsWith('EMP')) {
          pin = docId.replace('EMP', '');
        }

        // Get UUID from our mapping
        const uuid = this.getUUIDFromPin(pin);

        // 🆕 UPDATED: No attendance checking - just load employee data
        this.employeesCache.push({
          id: docId,
          name: data.employeeName || 'Unknown',
          designation: data.designation || 'No designation',
          department: data.department || 'No department',
          employeeNumber: data.employeeNumber || '',
          pin: pin,
          employeePin: pin,
          uuid: uuid || undefined
        });
      });

      // Sort by name for better UX
      this.employeesCache.sort((a, b) => a.name.localeCompare(b.name));
      
      console.log(`✅ Loaded ${this.employeesCache.length} employees from MasterSheet (attendance will be validated when data syncs)`);
    } catch (error) {
      console.error('❌ Error loading from MasterSheet:', error);
      throw error;
    }
  }

  /**
   * Get UUID from PIN using pre-built mapping
   */
  private getUUIDFromPin(pin: string): string | null {
    if (!pin) return null;
    
    const cleanPin = pin.toString().trim();
    
    // Try exact match first
    if (this.pinToUUIDMap.has(cleanPin)) {
      return this.pinToUUIDMap.get(cleanPin) || null;
    }
    
    // Try without leading zeros
    const pinWithoutZeros = cleanPin.replace(/^0+/, '') || '0';
    if (this.pinToUUIDMap.has(pinWithoutZeros)) {
      return this.pinToUUIDMap.get(pinWithoutZeros) || null;
    }
    
    // Try with leading zero
    if (this.pinToUUIDMap.has(`0${cleanPin}`)) {
      return this.pinToUUIDMap.get(`0${cleanPin}`) || null;
    }
    
    return null;
  }

  /**
   * 🆕 UPDATED: Get all employees WITHOUT attendance checking
   */
  async getEligibleEmployees(): Promise<NoBreakEmployee[]> {
    try {
      console.log('📋 Getting eligible employees for no-break (NO attendance check)...');
      
      // Initialize service if needed
      await this.initializeService();
      
      // Return cached data immediately
      console.log(`✅ Returning ${this.employeesCache.length} cached employees (validation deferred)`);
      return [...this.employeesCache];
      
    } catch (error) {
      console.error('❌ Error fetching eligible employees:', error);
      throw new Error(`Failed to fetch eligible employees: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 🆕 UPDATED: Get employees with progress callback WITHOUT attendance check
   */
  async getEligibleEmployeesWithProgress(
    progressCallback?: (current: number, total: number, stage: string) => void
  ): Promise<NoBreakEmployee[]> {
    try {
      console.log('📋 Getting eligible employees with progress (NO attendance check)...');
      
      const now = Date.now();
      
      // Check if cache is still valid
      if (this.employeesCache.length > 0 && (now - this.lastCacheUpdate) < this.CACHE_TTL) {
        console.log('Using cached employee data');
        return [...this.employeesCache];
      }

      // Prevent multiple simultaneous initializations
      if (this.isInitializing) {
        console.log('Initialization already in progress, waiting...');
        while (this.isInitializing) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        return [...this.employeesCache];
      }

      this.isInitializing = true;

      try {
        console.log('🚀 Initializing No Break service with progress tracking...');
        
        // Step 1: Build PIN -> UUID mapping (0-40%)
        progressCallback?.(10, 100, 'Building UUID mapping...');
        await this.buildPinToUUIDMap();
        progressCallback?.(40, 100, 'UUID mapping complete');
        
        // Step 2: Load employees from MasterSheet (40-90%)
        progressCallback?.(50, 100, 'Loading employee data...');
        await this.loadEmployeesFromMasterSheet();
        progressCallback?.(90, 100, 'Employee data loaded');
        
        // 🔄 REMOVED: No attendance checking
        
        progressCallback?.(100, 100, 'Ready!');
        
        this.lastCacheUpdate = now;
        console.log(`✅ Service initialized with ${this.employeesCache.length} employees`);
        
        return [...this.employeesCache];
        
      } finally {
        this.isInitializing = false;
      }
      
    } catch (error) {
      console.error('❌ Error fetching eligible employees:', error);
      throw new Error(`Failed to fetch eligible employees: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 🆕 UPDATED: Search employee WITHOUT attendance check
   */
  async searchEmployeeByPin(pin: string): Promise<NoBreakEmployee | null> {
    try {
      console.log(`🔍 Searching for PIN: ${pin}`);
      
      // Ensure service is initialized
      await this.initializeService();
      
      const normalizedPin = pin.toLowerCase().trim();
      
      // Search in cached employees
      const employee = this.employeesCache.find(emp => {
        const empPin = (emp.pin || '').toLowerCase();
        const empNumber = (emp.employeeNumber || '').toLowerCase();
        const empId = emp.id.toLowerCase();
        
        return empPin === normalizedPin ||
               empNumber === normalizedPin ||
               empId === normalizedPin ||
               empId === `emp${normalizedPin}` ||
               empId.replace(/^emp/i, '') === normalizedPin;
      });

      if (employee) {
        console.log(`✅ Found employee: ${employee.name}`);
        
        // If employee doesn't have UUID, try to get it
        if (!employee.uuid) {
          employee.uuid = this.getUUIDFromPin(employee.pin || '') || undefined;
        }
      } else {
        console.log(`❌ No employee found for PIN: ${normalizedPin}`);
      }

      return employee || null;
    } catch (error) {
      console.error('❌ Error searching employee by PIN:', error);
      return null;
    }
  }

  /**
   * 🆕 UPDATED: Apply no-break with DEFERRED validation
   */
  async applyNoBreakStatus(requestData: NoBreakRequestData): Promise<NoBreakResult> {
    try {
      console.log('🔄 Creating no-break request with deferred validation...');

      // Validation
      if (!requestData.selectedDate) {
        throw new Error('Date selection is required');
      }
      if (requestData.employeeIds.length === 0) {
        throw new Error('At least one employee must be selected');
      }
      if (!requestData.reason?.trim()) {
        throw new Error('Reason for no-break is required');
      }
      if (!requestData.projectName?.trim()) {
        throw new Error('Project name is required');
      }
      if (!requestData.projectCode?.trim()) {
        throw new Error('Project code is required');
      }

      // Ensure service is initialized
      await this.initializeService();

      // Get employee details
      const employeeDetails = await this.getEmployeeDetails(requestData.employeeIds);

      // Calculate validation time (when offline data will sync)
      const validationScheduledFor = this.getNextValidationTime(requestData.selectedDate);

      // 🆕 NEW: Create validation tracking (like overtime)
      const validationTracking = {
        isValidated: false,
        validationPending: true,
        validatedAt: null,
        validationResults: requestData.employeeIds.map(empId => {
          const emp = employeeDetails.find(e => e.id === empId);
          return {
            employeeId: empId,
            employeeName: emp?.name || 'Unknown',
            employeePin: emp?.pin || '',
            hasAttendance: false,
            status: 'pending' as const,
            attendanceRecord: null,
            validatedAt: null,
            noBreakApplied: false,
            uuid: emp?.uuid || null
          };
        }),
        presentCount: 0,
        absentCount: 0,
        appliedCount: 0,
        failedCount: 0,
        validationScheduledFor: Timestamp.fromDate(validationScheduledFor)
      };

      // Create no-break request document
      const noBreakRequestData = {
        projectName: requestData.projectName.trim(),
        projectCode: requestData.projectCode.trim(),
        selectedDate: Timestamp.fromDate(requestData.selectedDate),
        employeeIds: requestData.employeeIds,
        employeeDetails: employeeDetails,
        requesterId: requestData.requesterId,
        requesterName: requestData.requesterName,
        reason: requestData.reason.trim(),
        shift: requestData.shift || 'General',
        
        // Counts
        totalEmployees: requestData.employeeIds.length,
        
        // Status tracking
        status: 'pending_validation', // Will be 'completed' after validation
        
        // 🆕 NEW: Validation tracking
        validationTracking: validationTracking,
        willValidateOn: Timestamp.fromDate(validationScheduledFor),
        
        // Timestamps
        requestTime: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        
        // Metadata
        metadata: {
          source: 'web_portal',
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          validationDeferred: true,
          validationScheduledFor: validationScheduledFor.toISOString(),
          selectedDateString: requestData.selectedDate.toISOString().split('T')[0]
        }
      };

      console.log('💾 Saving no-break request with deferred validation...');
      console.log(`⏰ Validation scheduled for: ${validationScheduledFor.toLocaleString()}`);

      // Save to Firestore
      const docRef = await addDoc(collection(db, 'no_break_requests'), noBreakRequestData);

      console.log('✅ No-break request created with ID:', docRef.id);

      // Send notification
      await this.sendNotification({
        requestId: docRef.id,
        requesterId: requestData.requesterId,
        requesterName: requestData.requesterName,
        projectName: requestData.projectName,
        employeeCount: requestData.employeeIds.length,
        selectedDate: requestData.selectedDate,
        validationScheduledFor: validationScheduledFor
      });

      // Clear cache
      this.clearCache();

      return {
        success: true,
        requestId: docRef.id,
        message: `No-break request created successfully for ${requestData.employeeIds.length} employee(s)! Validation will occur automatically when offline attendance data syncs (usually overnight).`,
        processedCount: requestData.employeeIds.length
      };

    } catch (error) {
      console.error('❌ Error creating no-break request:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create no-break request',
        processedCount: 0
      };
    }
  }

  /**
   * Calculate next validation time
   */
  private getNextValidationTime(selectedDate: Date): Date {
    const validationDate = new Date(selectedDate);
    validationDate.setDate(validationDate.getDate() + 1);
    validationDate.setHours(6, 0, 0, 0); // Next day 6 AM
    return validationDate;
  }

  /**
   * Get employee details
   */
  private async getEmployeeDetails(employeeIds: string[]): Promise<Array<{
    id: string;
    name: string;
    designation: string;
    department: string;
    employeeNumber: string;
    pin: string;
    uuid?: string;
  }>> {
    const employeeDetails: Array<{
      id: string;
      name: string;
      designation: string;
      department: string;
      employeeNumber: string;
      pin: string;
      uuid?: string;
    }> = [];

    try {
      await this.initializeService();
      
      for (const empId of employeeIds) {
        const employee = this.employeesCache.find(emp => emp.id === empId);
        if (employee) {
          employeeDetails.push({
            id: empId,
            name: employee.name,
            designation: employee.designation,
            department: employee.department,
            employeeNumber: employee.employeeNumber || empId,
            pin: employee.pin || '',
            uuid: employee.uuid
          });
        }
      }
    } catch (error) {
      console.error('Error getting employee details:', error);
    }

    return employeeDetails;
  }

  /**
   * Send notification
   */
  private async sendNotification(data: {
    requestId: string;
    requesterId: string;
    requesterName: string;
    projectName: string;
    employeeCount: number;
    selectedDate: Date;
    validationScheduledFor: Date;
  }): Promise<void> {
    try {
      console.log('📧 Sending no-break request notification...');
      
      const notificationsRef = collection(db, 'notifications');
      const dateString = data.selectedDate.toLocaleDateString();
      const validationTimeString = data.validationScheduledFor.toLocaleString();
      
      // Confirmation notification for requester
      await addDoc(notificationsRef, {
        type: 'no_break_request_submitted',
        targetUserId: data.requesterId,
        title: 'No-Break Request Submitted',
        body: `Your no-break request for ${data.employeeCount} employees in ${data.projectName} on ${dateString} has been submitted. Validation will occur automatically when attendance data syncs (scheduled for ${validationTimeString}).`,
        data: {
          requestId: data.requestId,
          projectName: data.projectName,
          employeeCount: data.employeeCount.toString(),
          selectedDate: dateString,
          validationScheduledFor: validationTimeString
        },
        timestamp: serverTimestamp(),
        processed: false
      });

      console.log('✅ Notification sent successfully');
    } catch (error) {
      console.error('❌ Error sending notification:', error);
    }
  }

  /**
   * Get employee attendance record (for manual checks if needed)
   */
  async getEmployeeAttendanceRecord(employeeUUID: string, date: Date): Promise<AttendanceRecord | null> {
    try {
      const dateStr = this.formatDateString(date);
      const recordId = `${employeeUUID}-${dateStr}`;
      const recordRef = doc(db, 'Attendance_Records', 'PTSEmployees', 'Records', recordId);
      
      const recordDoc = await getDoc(recordRef);

      if (recordDoc.exists()) {
        const data = recordDoc.data();
        return {
          employeeId: data.employeeId || employeeUUID,
          date: data.date,
          checkIn: data.checkIn?.toDate(),
          checkInLocation: data.checkInLocation,
          workStatus: data.workStatus || 'Unknown',
          noBreak: data.noBreak || false,
          noBreakReason: data.noBreakReason,
          noBreakAppliedBy: data.noBreakAppliedBy,
          noBreakAppliedAt: data.noBreakAppliedAt?.toDate(),
          lastUpdated: data.lastUpdated?.toDate(),
          ...data
        };
      }

      return null;
    } catch (error) {
      console.error('❌ Error fetching attendance record:', error);
      return null;
    }
  }

  /**
   * Check existing no-break status (optional)
   */
  async checkNoBreakStatus(employeeId: string, date: Date): Promise<{
    hasNoBreak: boolean;
    reason?: string;
    appliedBy?: string;
    appliedAt?: Date;
  }> {
    try {
      await this.initializeService();
      
      const employee = this.employeesCache.find(emp => emp.id === employeeId);
      
      if (!employee || !employee.uuid) {
        return { hasNoBreak: false };
      }

      const record = await this.getEmployeeAttendanceRecord(employee.uuid, date);
      
      if (record && record.noBreak) {
        return {
          hasNoBreak: true,
          reason: record.noBreakReason,
          appliedBy: record.noBreakAppliedBy,
          appliedAt: record.noBreakAppliedAt
        };
      }

      return { hasNoBreak: false };
    } catch (error) {
      console.error('❌ Error checking no-break status:', error);
      return { hasNoBreak: false };
    }
  }

  /**
   * Validate date selection
   */
  validateDateSelection(date: Date): { valid: boolean; error?: string } {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const selectedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (selectedDate > today) {
      return {
        valid: false,
        error: 'Cannot select future dates'
      };
    }
    
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    if (selectedDate < thirtyDaysAgo) {
      return {
        valid: false,
        error: 'Cannot select dates older than 30 days'
      };
    }
    
    return { valid: true };
  }

  /**
   * Format date string
   */
  private formatDateString(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  /**
   * Force refresh cache
   */
  async forceRefresh(): Promise<void> {
    console.log('🔄 Force refreshing no-break service cache...');
    this.lastCacheUpdate = 0;
    this.employeesCache = [];
    this.pinToUUIDMap.clear();
    await this.initializeService();
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.employeesCache = [];
    this.pinToUUIDMap.clear();
    this.lastCacheUpdate = 0;
    console.log('✅ No-break service cache cleared');
  }
}

export const noBreakService = new NoBreakService();